import type { StorageUsage } from '@ralph/contracts'
import clsx from 'clsx'
import { Boxes, Database, HardDrive, Layers, TriangleAlert, User, UsersRound } from 'lucide-react'
import type { CSSProperties, ReactNode } from 'react'
import { Button, Meter, Panel, Spinner } from '@/components/ui/primitives.tsx'
import { useI18n } from '@/i18n/index.ts'
import { formatShare, fullestLimitedBase, rankedSources, vectorShare, volumeReading } from './storage-presentation.ts'

/**
 * Where the bytes are (PRD-14 OBS-016).
 *
 * Four stores, four separate readings, because they fill for different reasons and are relieved
 * by different actions — and because a single "storage used" figure is the number an operator
 * can do the least with. Documents grow when people upload; the vector index grows when anything
 * is re-chunked or re-embedded, without a single upload; artifacts grow with runs; the volume
 * underneath them is shared with everything else on the host, including the database itself.
 *
 * The card refuses two temptations. It never draws a bar against a limit that does not exist —
 * only the artifact volume and each knowledge base's own storage limit are real ceilings, so only
 * those two get gauges. And it never renders an unreadable filesystem as an empty one: a volume that could
 * not be measured says so, because "we could not read the disk" and "the disk is empty" leading
 * to the same picture is how a full disk goes unnoticed.
 */
export const StoragePanel = ({
  storage,
  loading,
  failed,
  onRetry
}: {
  storage: StorageUsage | undefined
  loading: boolean
  failed: boolean
  onRetry: () => void
}) => {
  // Destructured INSIDE the body, never at module scope: a module-scope capture of a bound
  // formatter freezes the locale at import time and keeps rendering the previous language
  // after a switch, with nothing failing (PRD-27 F-3).
  const i18n = useI18n()
  const { formatAgo, formatBytes } = i18n
  const t = i18n.t.activity.storage

  if (failed) {
    return (
      <Panel className="mt-3">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3.5">
          <p role="alert" className="min-w-0 flex-1 text-[13px] leading-relaxed text-alert">
            {t.failed}
          </p>
          <Button variant="mono" size="sm" onClick={onRetry}>
            {i18n.t.common.retry}
          </Button>
        </div>
      </Panel>
    )
  }

  if (loading || !storage) {
    return (
      <Panel role="status" className="mt-3 flex items-center gap-2 px-4 py-6 text-[13px] text-fg-subtle">
        <Spinner />
        {t.busy}
      </Panel>
    )
  }

  const volume = volumeReading(storage.artifacts.volume)
  const fullest = fullestLimitedBase(storage)
  const sources = rankedSources(storage)
  const indexed = storage.documents.indexedCount
  // One reading drives both the bar and the words beside it. Computing the share twice — once
  // for each — is how a card ends up drawing 7% next to the word "unknown".
  const share = vectorShare(storage)

  return (
    <section className="mt-3">
      <div className="mb-2 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <h2 className="eyebrow">{t.title}</h2>
        <span className="readout text-fg-subtle">{t.measured(formatAgo(storage.measuredAt))}</span>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {/*
          Documents. No platform-wide ceiling exists — the limit is per base, and a group's is
          larger than a person's — so the gauge is the base closest to ITS limit, which is the one
          that will actually refuse an upload next.
        */}
        <StoreCard
          tone="grounded"
          icon={<Database size={12} strokeWidth={2.1} aria-hidden />}
          label={t.documents}
          value={formatBytes(storage.documents.bytes)}
          footnote={t.documentsCensus(i18n, {
            files: storage.documents.documentCount,
            bases: storage.documents.sourceCount,
            indexed,
            unreadable: storage.documents.failedCount
          })}
        >
          {fullest ? (
            <Meter
              label={t.fullestBase}
              value={t.fullestValue(
                formatShare(i18n, fullest.capacity.fill) ?? i18n.t.common.none,
                formatBytes(fullest.capacity.limit ?? 0)
              )}
              fill={fullest.capacity.fill}
              tone={fullest.capacity.pressure === 'calm' ? 'grounded' : 'alert'}
              detail={
                <>
                  <span className="truncate">{fullest.name ?? t.unnamedBaseOf(fullest.ownerName)}</span>
                  {fullest.capacity.pressure !== 'calm' && <span className="ml-1.5 text-alert">{t.nearItsLimit}</span>}
                </>
              }
            />
          ) : (
            <p className="readout text-fg-subtle">{t.noLimitedBase}</p>
          )}
        </StoreCard>

        {/*
          The vector index. The share of the database is the reading that matters here: it is the
          one number that moves when an embedding model changes and nothing else does.
        */}
        <StoreCard
          tone="primary"
          icon={<Layers size={12} strokeWidth={2.1} aria-hidden />}
          label={t.vectorIndex}
          value={formatBytes(storage.vectors.totalBytes)}
          footnote={t.vectorCensus(i18n, {
            passages: storage.vectors.passageCount,
            dimensions: storage.vectors.dimensions,
            notEmbedded: Math.max(0, storage.vectors.passageCount - storage.vectors.embeddedCount)
          })}
        >
          <Meter
            label={t.shareOfDatabase}
            value={formatShare(i18n, share) ?? t.unknownShare}
            fill={share}
            detail={t.vectorBreakdown(
              formatBytes(storage.vectors.indexBytes),
              formatBytes(storage.vectors.payloadBytes),
              formatBytes(storage.database.totalBytes)
            )}
          />
        </StoreCard>

        {/* Artifacts, and the only true capacity reading on the screen: the volume itself. */}
        <StoreCard
          tone="primary"
          icon={<Boxes size={12} strokeWidth={2.1} aria-hidden />}
          label={t.artifacts}
          value={formatBytes(storage.artifacts.bytes)}
          footnote={t.producedByRuns(i18n, storage.artifacts.artifactCount)}
        >
          {volume ? (
            <Meter
              label={t.volume}
              value={t.volumeUsed(formatShare(i18n, volume.fill) ?? i18n.t.common.none)}
              fill={volume.fill}
              tone={volume.fill >= 0.9 ? 'alert' : 'primary'}
              detail={t.freeOfTotal(formatBytes(volume.freeBytes), formatBytes(volume.totalBytes))}
            />
          ) : (
            <p className="readout flex items-start gap-1.5 text-fg-subtle">
              <TriangleAlert size={11} strokeWidth={2.2} className="mt-[2px] shrink-0" aria-hidden />
              {t.volumeUnreadable}
            </p>
          )}
        </StoreCard>

        {/* The database as a whole, plus the one number here that is a decision rather than a fact. */}
        <StoreCard
          tone={storage.documents.reclaimableBytes > 0 ? 'alert' : 'primary'}
          icon={<HardDrive size={12} strokeWidth={2.1} aria-hidden />}
          label={t.database}
          value={formatBytes(storage.database.totalBytes)}
          footnote={storage.database.name}
        >
          {storage.documents.reclaimableCount > 0 ? (
            <p className="readout text-pretty leading-[1.45] text-fg-subtle">
              <span className="text-alert">{t.reclaimable(formatBytes(storage.documents.reclaimableBytes))}</span> —{' '}
              {t.stillStored(i18n, storage.documents.reclaimableCount)}
            </p>
          ) : (
            <p className="readout text-fg-subtle">{t.nothingReclaimable}</p>
          )}
        </StoreCard>
      </div>

      <Panel className="mt-3 overflow-hidden">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-rule px-4 py-3">
          <Database size={12} strokeWidth={2.1} className="shrink-0 text-fg-muted" aria-hidden />
          <h3 className="eyebrow">{t.tableTitle}</h3>
          <span className="readout ml-auto text-fg-muted">
            {t.shownOfBases(i18n, sources.length, storage.documents.sourceCount)}
          </span>
        </div>

        {/*
          The one surprising column, explained once. Embeddings are routinely several times the
          size of the file they came from — a 3 KB note becomes eight vectors of 1.5 KB each —
          and an operator who reads that as a bug goes looking for one.
        */}
        <p className="readout border-b border-rule px-4 py-2 text-fg-subtle">{t.embeddingsNote}</p>

        {sources.length === 0 ? (
          <p className="px-4 py-3.5 text-[13px] leading-relaxed text-fg-subtle">{t.nothingIndexed}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[34rem] text-[13px]">
              <thead>
                <tr className="border-b border-rule text-left">
                  <th className="eyebrow px-4 py-2.5 font-normal">{t.columns.base}</th>
                  <th className="eyebrow px-2 py-2.5 text-right font-normal">{t.columns.files}</th>
                  <th className="eyebrow px-2 py-2.5 text-right font-normal">{t.columns.passages}</th>
                  <th className="eyebrow px-2 py-2.5 text-right font-normal">{t.columns.embeddings}</th>
                  <th className="eyebrow px-2 py-2.5 text-right font-normal">{t.columns.size}</th>
                  <th className="eyebrow w-[132px] px-4 py-2.5 font-normal">{t.columns.share}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-rule">
                {sources.map((source) => (
                  <tr key={source.sourceId} className="align-middle transition-colors duration-200 hover:bg-card-solid">
                    <td className="px-4 py-2.5">
                      {/* A base the reader cannot use is shown by what it occupies and whose it is,
                          never by name (PRD-26 D-54): the owner is who to ask about it. */}
                      <span className="flex min-w-0 items-center gap-1.5">
                        {source.name === null ? (
                          <span className="truncate italic text-fg-muted">{t.baseYouCannotOpen}</span>
                        ) : (
                          <span className="truncate font-[550] tracking-[-0.014em]">{source.name}</span>
                        )}
                      </span>
                      <span className="readout mt-0.5 flex min-w-0 items-center gap-1 text-fg-subtle">
                        {source.ownerKind === 'group' ? (
                          <UsersRound size={10} strokeWidth={2.2} className="shrink-0" aria-hidden />
                        ) : (
                          <User size={10} strokeWidth={2.2} className="shrink-0" aria-hidden />
                        )}
                        <span className="truncate">
                          {/* The prefix is read only to a screen reader, and the space after it
                              belongs to the view rather than to the entry — a catalogue string
                              that ends in a space is a fragment waiting to be mis-trimmed. */}
                          <span className="sr-only">{source.ownerKind === 'group' ? t.ownedByGroup : t.ownedBy} </span>
                          {source.ownerName || t.noOwner}
                          {source.lastIngestedAt ? ` · ${t.lastAdded(formatAgo(source.lastIngestedAt))}` : ''}
                        </span>
                      </span>
                    </td>
                    <td className="readout px-2 py-2.5 text-right">{i18n.formatCount(source.documentCount)}</td>
                    <td className="readout px-2 py-2.5 text-right">{i18n.formatCount(source.passageCount)}</td>
                    <td className="readout px-2 py-2.5 text-right text-fg-muted">
                      {formatBytes(source.vectorPayloadBytes)}
                    </td>
                    <td className="readout px-2 py-2.5 text-right font-medium">{formatBytes(source.bytes)}</td>
                    <td className="px-4 py-2.5">
                      {/* Share of what is listed, so the bars and the rows agree; the base's own
                          limit, where it has one, is the sentence underneath. */}
                      <span aria-hidden className="block h-[6px] w-full overflow-hidden rounded-full bg-rule">
                        <span
                          style={{ width: `${Math.max(2, source.share * 100)}%` }}
                          className={clsx(
                            'block h-full rounded-full',
                            source.capacity.pressure === 'calm' ? 'bg-primary' : 'bg-alert'
                          )}
                        />
                      </span>
                      <span className="readout mt-1 block text-fg-subtle">
                        {formatShare(i18n, source.share)}
                        {source.capacity.fill !== null && source.capacity.pressure !== 'calm'
                          ? ` · ${t.ofItsLimit(formatShare(i18n, source.capacity.fill) ?? i18n.t.common.none)}`
                          : ''}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </section>
  )
}

/**
 * One store.
 *
 * The headline is the level, the gauge underneath is the capacity reading where one exists, and
 * the footnote is the census that explains the level. Same wash treatment as the metric cards
 * above it, so the two rows read as one instrument panel rather than two features.
 */
const StoreCard = ({
  label,
  value,
  footnote,
  icon,
  tone,
  children
}: {
  label: string
  value: string
  footnote: string
  icon: ReactNode
  tone: 'primary' | 'grounded' | 'alert'
  children: ReactNode
}) => (
  <div
    style={{ '--wash-accent': `var(--${tone})` } as CSSProperties}
    className="card accent-wash flex flex-col rounded-card px-[15px] pb-[15px] pt-[14px]"
  >
    <div
      className={clsx(
        'flex items-center gap-1.5',
        tone === 'grounded' && 'text-grounded',
        tone === 'alert' && 'text-alert',
        tone === 'primary' && 'text-primary'
      )}
    >
      {icon}
      <span className="eyebrow tracking-[0.11em] text-fg-muted">{label}</span>
    </div>

    <span className="mt-[5px] font-mono text-[25px] font-medium tabular-nums tracking-[-0.028em]">{value}</span>
    <p className="readout mt-1 text-pretty leading-[1.45] text-fg-subtle">{footnote}</p>

    <div className="mt-auto pt-3">{children}</div>
  </div>
)
